import {
  convertQueryCommandToQueryString,
  IQueryCommandOptions,
} from '@llws/dynamic-router-utils';
import { ITourBookingResponses } from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import vm from '@/main';
import {
  capitalize,
  decodeHtml,
  IApiResponse,
  IFilterPayload,
  IMetaResponse,
  IModifyPayload,
} from '@/shared';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { ICalendarEvent } from '@/shared/interfaces/calendar-event';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { ITourBookingPayload } from '@/shared/interfaces/tours';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { tToursListSelectedFilters } from '@/shared/types/t-tours-filters';

interface IToursState {
  list: IApiResponse<ITourBookingResponses[], IMetaResponse> | null;
  single: IApiResponse<ITourBookingResponses, IMetaResponse> | null;
  toursBookings: Array<ICalendarEvent>,
  toursListMeta: IMetaResponse,
  toursListSelectedFilters: tToursListSelectedFilters,
  sources: string[];
  fullSources: string[];
}

// Base store configuration
const agentGatewayResourcePathAgent: string = '/tour-api-agent/tour_booking_responses';
const adminGatewayResourcePathAdmin: string = '/tour-api-admin/tour_booking_responses';
const listViewName: string = '';
const showLogging: boolean = false;

const defaultGetListFilters: IQueryCommandOptions = {
  alias: 'tbr',
  relations: [
    {
      path: 'tbr.building',
      alias: 'b',
    },
    {
      path: 'tbr.user',
      alias: 'u',
    },
    {
      path: 'tbr.city',
      alias: 'c',
    },
  ],
  select: [
    'tbr.id',
    'tbr.buildingId',
    'tbr.userId',
    'tbr.followUpStatus',
    'tbr.startTime',
    'tbr.endTime',
    'tbr.source',
    'tbr.fullSource',
    'tbr.utmSource',
    'tbr.spamScore',
    'tbr.comments',
    'tbr.name',
    'tbr.email',
    'tbr.phone',
    'tbr.otherData',
    'tbr.clientId',
    'tbr.rating',
    'tbr.tourType',
    'tbr.virtualLink',
    'tbr.streetNumber',
    'tbr.streetName',
    'tbr.cityId',
    'c.cityName',
    'tbr.postal',
    'b.buildingName',
    'b.clientId',
    'u.id',
    'u.firstName',
    'u.lastName',
    'tbr.created',
  ],
};
const defaultGetSingleFilters: IQueryCommandOptions = {};

// Instantiate the base store with the params
const base = new StoreBaseImplementationV2<ITourBookingResponses>({
  adminGatewayResourcePath: adminGatewayResourcePathAdmin,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

const tourUserHasBuildingsFilters: IQueryCommandOptions = {
  relations: [
    {
      alias: 'user',
      path: 'user',
    },
    {
      alias: 'building',
      path: 'building',
    },
  ],
  limit: 9999,
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    toursBookings: [] as Array<ICalendarEvent>,
    toursListMeta: {} as IMetaResponse,
    toursListSelectedFilters: {} as tToursListSelectedFilters,
    sources: [] as string[],
    fullSources: [] as string[],
  },

  getters: {
    ...base.getters,
    toursBookings(state: IToursState): ICalendarEvent[] {
      return state.toursBookings;
    },

    toursListMeta(state: IToursState) {
      return state.toursListMeta;
    },

    toursListSelectedFilters(state: IToursState) {
      return state.toursListSelectedFilters;
    },

    sources(state: IToursState) {
      return state.sources;
    },

    fullSources(state: IToursState) {
      return state.fullSources;
    },
  },

  mutations: {
    ...base.mutations,
    setToursBookings: (state: IToursState, payload: IApiResponse<ITourBookingResponses[], IMetaResponse>) => {
      state.toursBookings = payload.data?.map((tour) => ({
        id: tour.id,
        name: `${capitalize(tour.tourType.split('-').join(' '))} Tour - ${decodeHtml(tour.building?.buildingName)} - ${tour.name || 'Unknown'}`,
        start: dayjs(tour.startTime).format('YYYY-MM-DD hh:mm'),
        end: dayjs(tour.endTime).format('YYYY-MM-DD hh:mm'),
        eventCustomDetails: {
          ...tour,
          agentName: `${tour.user?.firstName} ${tour.user?.lastName}`,
          propertyName: decodeHtml(tour.building?.buildingName),
        },
        source: tour.source,
      } as ICalendarEvent)) || [];
    },
    setToursListMeta(state: IToursState, payload: IMetaResponse) {
      state.toursListMeta = payload;
    },
    setToursListSelectedFilters(state: IToursState, payload: tToursListSelectedFilters) {
      state.toursListSelectedFilters = payload;
    },
    setSources(state: IToursState, payload: string[]) {
      state.sources = payload;
    },
    setFullSources(state: IToursState, payload: string[]) {
      state.fullSources = payload;
    },
  },

  actions: {
    ...base.actions,
    search: async (ctx: IAction<IStateStrict<ITourBookingResponses>>, payload: IQueryCommandOptions) => {
      const result = await adminRequest.post<ITourBookingResponses[]>(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/_search`, {
        ...defaultGetListFilters,
        ...payload,
        alias: 'tbr',
      });
      ctx.commit('setToursBookings', result);
      ctx.commit('setToursListMeta', result.meta);
      ctx.commit('set', result.data);
      return result;
    },
    getTourBookingResponses: async (ctx: IAction<IStateStrict<ITourBookingResponses>>, payload: IQueryCommandOptions) => {
      const where = payload.where ?? [];
      const queryString: IQueryCommandOptions = payload ? { ...defaultGetListFilters, ...payload, where } : { ...defaultGetListFilters, where };
      const response = await adminRequest.get(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}${convertQueryCommandToQueryString(queryString)}`);
      ctx.commit('setToursBookings', response);
      ctx.commit('setToursListMeta', response.meta);
      ctx.commit('set', response.data);
    },
    getTourBookingResponse: async (ctx: IAction<IStateStrict<ITourBookingResponses>>, payload?: IFilterPayload) => {
      const queryString = (payload?.filters
        ? { ...tourUserHasBuildingsFilters, ...payload.filters }
        : tourUserHasBuildingsFilters) as IQueryCommandOptions;

      const response = await adminRequest.get(`${vm()?.$rsGlobals.$userHasAtLeastOneAccess('tour_admins', 'read')
        ? adminGatewayResourcePathAdmin : agentGatewayResourcePathAgent}/${payload?.id}${convertQueryCommandToQueryString(queryString)}`);
      ctx.commit('set', response.data);
    },
    addTour: async (ctx: IAction<Record<string, any>>, payload: ITourBookingPayload) => {
      const response = await adminRequest.post(`${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}`, payload);
      return response;
    },
    cancelTour: async (ctx: IAction<Record<string, any>>, payload: number | string) => {
      const response = await adminRequest.put(
        `${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}/${payload}`,
        {
          followUpStatus: 'cancel',
        },
      );
      return response;
    },
    updateTourBookingResponse: async (ctx: IAction<IStateStrict<ITourBookingResponses>>, payload?: IModifyPayload<number, ITourBookingResponses>) => {
      const result = await adminRequest.put(`${vm()?.$rsGlobals.$userHasAtLeastOneAccess('tour_admins', 'read')
        ? adminGatewayResourcePathAdmin : agentGatewayResourcePathAgent}/${payload?.id}`, payload!.data);
      ctx.commit('set', result.data);
    },

    getSources: async (ctx: IAction<IStateStrict<ITourBookingResponses>>) => {
      const url = `${vm()?.$rsGlobals.$hasToursRestricted() ? agentGatewayResourcePathAgent : adminGatewayResourcePathAdmin}`;

      const sourceFilters: IQueryCommandOptions = {
        selectDistinct: 'source',
      };
      const fullSourceFilters: IQueryCommandOptions = {
        selectDistinct: 'fullSource',
      };

      const [sourceResult, fullSourceResult] = await Promise.all([
        adminRequest.get(`${url}${convertQueryCommandToQueryString(sourceFilters)}`),
        adminRequest.get(`${url}${convertQueryCommandToQueryString(fullSourceFilters)}`),
      ]);

      ctx.commit('setSources', sourceResult.data.map((tourBookingResponse: ITourBookingResponses) => tourBookingResponse.source));
      ctx.commit('setFullSources', fullSourceResult.data.map((tourBookingResponse: ITourBookingResponses) => tourBookingResponse.fullSource));
    },
  },
};
